import React, { useState, useRef, useEffect } from "react";
import { Box, Flex, Text, Icon, useOutsideClick } from "@chakra-ui/react";
import { MdAccessTime } from "react-icons/md";

const CustomTimePicker = ({ value, onChange, placeholder = "Select time" }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [hour, setHour] = useState(null);
  const [minute, setMinute] = useState(null);
  const [ampm, setAmpm] = useState(null);

  const pickerRef = useRef(null);
  const hourRefs = useRef({});
  const minuteRefs = useRef({});
  const ampmRefs = useRef({});

  useOutsideClick({
    ref: pickerRef,
    handler: () => setShowDropdown(false),
  });

  useEffect(() => {
    if (showDropdown && value) {
      const [h, mPart] = value.split(":");
      const [m, am] = (mPart || "").split(" ");
      setHour(parseInt(h) || null);
      setMinute(parseInt(m) || null);
      setAmpm(am || null);
    }
  }, [showDropdown, value]);

  useEffect(() => {
    if (hour !== null && minute !== null && ampm !== null) {
      const formatted = `${hour
        .toString()
        .padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${ampm}`;
      onChange(formatted);
    }
  }, [hour, minute, ampm]);

  useEffect(() => {
    if (showDropdown) {
      setTimeout(() => {
        hourRefs.current[hour]?.scrollIntoView({ block: "center" });
        minuteRefs.current[minute]?.scrollIntoView({ block: "center" });
        ampmRefs.current[ampm]?.scrollIntoView({ block: "center" });
      }, 0);
    }
  }, [showDropdown, hour, minute, ampm]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const TimeColumn = ({
    items,
    selected,
    onSelect,
    refMap,
    isAmPm = false,
  }) => (
    <Box
      maxH="250px"
      overflowY="auto"
      minW="30px"
      css={{
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {items.map((item) => (
        <Box
          key={item}
          ref={(el) => (refMap.current[item] = el)}
          px={3}
          py={2}
          mb={1}
          textAlign="center"
          borderRadius="md"
          cursor="pointer"
          fontWeight={selected === item ? "bold" : "normal"}
          bg={selected === item ? "teal.300" : "transparent"}
          _hover={{ bg: "teal.50" }}
          onClick={() => {
            onSelect(item);
            if (isAmPm) setShowDropdown(false); // 👈 CLOSE DROPDOWN ON AM/PM SELECT
          }}
        >
          {item.toString().padStart(2, "0")}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box position="relative" maxW="320px" ref={pickerRef}>
      <Flex
        align="center"
        justify="space-between"
        border="1px solid rgb(226, 232, 240)"
        // borderColor="gray.300"
        borderRadius="12px"
        px={4}
        py={2}
        bg="white"
        cursor="pointer"
        boxShadow="sm"
        _hover={{ boxShadow: "md" }}
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        <Text
          color={value ? "gray.700" : "gray.400"}
          fontSize="12px"
          flex="1"
          fontWeight="400"
        >
          {value || placeholder}
        </Text>

        <Icon as={MdAccessTime} color="teal.500" boxSize={5} ml={2} />
      </Flex>

      {showDropdown && (
        <Flex
          maxW="200x"
          position="absolute"
          top="100%"
          mt={2}
          zIndex={10}
          bg="white"
          boxShadow="xl"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          p={3}
          gap={4}
        >
          <TimeColumn
            items={hours}
            selected={hour}
            onSelect={setHour}
            refMap={hourRefs}
          />
          <TimeColumn
            items={minutes}
            selected={minute}
            onSelect={setMinute}
            refMap={minuteRefs}
          />
          <TimeColumn
            items={["AM", "PM"]}
            selected={ampm}
            onSelect={setAmpm}
            refMap={ampmRefs}
            isAmPm={true} // 👈 tell component to auto-close
          />
        </Flex>
      )}
    </Box>
  );
};

export default CustomTimePicker;
